import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@fluentui/react-components';
import { LineChart } from '@fluentui/react-charting';
import type { IChartProps, ILineChartPoints } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useUtilizationTrendChartStyles } from './UtilizationTrendChart.styles';
import type { UtilizationDataPoint } from '@/types/reports';

export interface UtilizationTrendChartProps {
  data: UtilizationDataPoint[];
}

const BASE_DATE = new Date(2024, 0, 1);
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const UtilizationTrendChart = ({ data }: UtilizationTrendChartProps) => {
  const styles = useUtilizationTrendChartStyles();
  const { t } = useTranslation('reports');

  const dateToLabel = useMemo(() => {
    const map = new Map<number, string>();
    data.forEach((point, idx) => {
      map.set(BASE_DATE.getTime() + idx * WEEK_MS, point.period);
    });
    return map;
  }, [data]);

  const chartData: IChartProps = useMemo(() => {
    const lineChartPoints: ILineChartPoints[] = [
      {
        legend: t('widgets.utilizationTrendTitle'),
        data: data.map((point, idx) => ({
          x: new Date(BASE_DATE.getTime() + idx * WEEK_MS),
          y: point.utilization,
          xAxisCalloutData: point.period,
          yAxisCalloutData: `${point.utilization}%`,
        })),
        color: tokens.colorBrandForeground1,
      },
    ];
    return { chartTitle: t('widgets.utilizationTrendTitle'), lineChartData: lineChartPoints };
  }, [data, t]);

  const xAxisTickValues = useMemo(() => {
    const interval = Math.max(1, Math.floor(data.length / 6));
    return data
      .map((_, i) => new Date(BASE_DATE.getTime() + i * WEEK_MS))
      .filter((_, i) => i % interval === 0);
  }, [data]);

  return (
    <WidgetContainer title={t('widgets.utilizationTrendTitle')}>
      <div className={styles.root} role="figure" aria-label={t('widgets.utilizationTrendTitle')}>
        <ChartThemeProvider>
        <LineChart
          data={chartData}
          height={240}
          yAxisTitle="%"
          tickValues={xAxisTickValues}
          customDateTimeFormatter={(date: Date) => dateToLabel.get(date.getTime()) ?? ''}
          enableReflow={true}
        />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

UtilizationTrendChart.displayName = 'UtilizationTrendChart';
